import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { InfiniteData } from '@tanstack/query-core';
import { hashKey, onlineManager, useQueryClient } from '@tanstack/react-query';

import NotificationKindChip from 'components/notifications/NotificationKindChip';
import { NotificationState } from 'components/shared/Notification';
import { Flags } from 'constants/flags';
import { useProfile } from 'context/profile';
import { useDebouncedWebsocketSubscription } from 'hooks/useDebouncedWebsocketSubscription';
import Notification from 'models/Notification';
import { notificationQueryKeys, NotificationsPage, useUpdateNotification } from 'services/api/notification';
import { useNotificationActions } from 'stores/notificationStore';

// The cable frame is a content-free refetch nudge (In-App Notifications tech design §4.6): it
// carries no payload BY DESIGN (the PHI boundary), so it is only ever a signal to refetch. Toast
// content comes from the refetch diff, never the frame.
type NotificationChannelMessage = { action: 'refetch' };

// Collapse a burst of nudges (a review with attachments fires several types at once; the
// local-midnight cron dispatch) into a single refetch — matches the chat/referral subscribers.
// Exported (with SUSPECT_GRACE_MS) so the spec pins the grace-outlasts-debounce invariant.
export const NUDGE_DEBOUNCE_MS = 300;

// New-notification toasts self-dismiss after 8s via the provider's animated slide-out (Taylor).
const NOTIFICATION_TOAST_TTL_MS = 8000;

// How long after a reconnect confirms that frames stay suspect. A dying socket's buffered frames
// flush BEFORE the fresh subscription confirms, but they queue in the nudge debounce and can fire
// after it — the grace must outlast the debounce plus delivery slack so a flushed batch can never
// slip through as live. Cost: a genuinely live frame in the first second after a reconnect
// converges silently (the bias is always against a stale burst).
export const SUSPECT_GRACE_MS = 1000;

type NotificationsFeedData = InfiniteData<NotificationsPage, string | undefined>;

/**
 * Live badge/inbox/toast updates for In-App Notifications (OLIO-3060, story 7). Subscribes the
 * per-user `NotificationChannel` (identity is the authorization — no params) through the debounced
 * websocket hook, gated on the `in_app_notifications` flag so an un-flagged user attempts no
 * subscription at all. On a nudge it invalidates the shared `notifications/index` prefix — one
 * invalidation covers the badge and both tabs, since the unread count is a `select` off the index
 * meta (there is no separate count key). Toasts come from a frame-triggered refetch's diff, onto
 * the AI-job slide-in stack — and only from those runs; see below.
 *
 * Mounted once, alongside `useEscalationWebSocket`, in the persistent `UserBar`.
 *
 * TOASTS FIRE ONLY FOR ROWS ANNOUNCED WHILE THE CABLE IS LIVE (milestone QA feedback): a cable
 * frame only ever reaches a live connection, so "a frame arrived" is itself the logged-in signal —
 * no payload needed. The seen-set converges continuously from the feed's cache entry (the initial
 * fetch, the hook's own refetches, and refetches the hook did NOT run — React Query's
 * reconnect/focus refetches, bell pagination), so rows accrued while logged out or disconnected
 * land in the badge/inbox without ever toasting; only a frame-triggered refetch toasts, and only
 * the rows its own run delivered. Frames themselves are vetted too: one received while the
 * transport is suspect (offline event or cable disconnect, until a connect or the browser's
 * online event signals recovery + grace) is a dying socket's buffered flush, not a live
 * announcement, and refetches silently. Without these guards the diff cannot tell "arrived live"
 * from "accrued while away", and logging in after a busy day replays every toast at once.
 */
export function useNotificationsWebSocket() {
  const { profile } = useProfile();
  const enabled = profile.hasFlag(Flags.InAppNotifications);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addNotification } = useNotificationActions();
  const updateNotification = useUpdateNotification();

  // The first connect only confirms the stream — the feed already fetched on mount, so there is
  // nothing to resync. A LATER connect is a reconnect after a cable drop; refetching then
  // converges anything missed while offline. NotificationChannel transmits nothing on subscribe,
  // so there is no subscribe-time frame to skip (unlike WorkflowChannel/ChatConversationChannel) —
  // the reconnect handshake is the only resync trigger, and every received frame is a real nudge.
  const everConnected = useRef(false);

  // Every notification id the feed's cache entry has ever held — kept current by the permanent
  // convergence subscription below, not just by the hook's own runs. `null` until the first fetch
  // populates the feed, so a nudge racing the login fetch can never diff against an empty
  // baseline (the burst-on-login bug). A frame-triggered run diffs against a SNAPSHOT of this set
  // taken at run start: the live set keeps converging while the refetch is in flight, and the
  // in-flight guard serializes runs, so two overlapping nudges can't double-toast a row.
  const seenIdsRef = useRef<Set<string> | null>(null);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);
  // The queued trailing run's toast intent. ANDed on purpose: if a silent request (a reconnect
  // resync) queues behind an in-flight refetch, the trailing run stays silent — a disconnect gap's
  // rows must never ride a frame's diff into toasts. The bias is always against a burst; the cost
  // is the toasts of every frame that queues behind that one silent request (they collapse into
  // the single silent trailing run) — bounded by one refetch's flight time.
  const pendingToastRef = useRef(false);

  // The SUSPECT WINDOW (the join-ref idea from Phoenix Channels, approximated client-side —
  // ActionCable frames carry no ref that ties them to a connection generation). A frame is only a
  // live announcement if the transport was believed healthy when it arrived: a dying socket
  // buffers the server's nudges and flushes them all on network restore as genuine onReceived
  // calls announcing rows accrued while away. So from the moment the browser goes offline or the
  // cable reports a disconnect, until SUSPECT_GRACE_MS after the next recovery signal (a
  // confirmed subscription connect, or the browser going back online — the cable can survive a
  // blip without ever reconnecting), every frame is downgraded to a silent refetch.
  // KNOWN RESIDUAL (accepted): a stall short enough to raise neither a browser offline event nor
  // the cable monitor's ~6s stale threshold has no opener, so a same-socket flush inside it
  // toasts its few seconds of rows — the same door, but a bounded one.
  // 0 = never suspect; Infinity = suspect until a recovery signal arrives.
  const suspectUntilRef = useRef(0);
  // Generation counter, bumped by both window OPENERS. A run captures it at start and refuses to
  // toast if it changed by settle time — a timestamp comparison alone is a point sample that
  // cannot see a window that opened AND closed inside one run's flight (frame starts a run, the
  // request stalls, the cable drops and reconnects, and the stalled response finally lands after
  // the grace carrying the whole gap).
  const suspectEpochRef = useRef(0);

  const openSuspectWindow = useCallback(() => {
    suspectUntilRef.current = Infinity;
    suspectEpochRef.current += 1;
  }, []);

  // A recovery signal schedules the close ONLY when a window is actually open (Infinity, or a
  // grace still running). A closed window stays exactly as it was — so a bare online event or a
  // duplicate connect on a healthy socket can never arm a fresh silent second.
  const closeSuspectWindow = useCallback(() => {
    const open = suspectUntilRef.current === Infinity || Date.now() < suspectUntilRef.current;
    if (open) suspectUntilRef.current = Date.now() + SUSPECT_GRACE_MS;
  }, []);

  // Converge EVERY row the feed's cache entry ever sees into the seen-set — the initial fetch
  // (the eager baseline, so the session's first frame toasts), the hook's own refetches, and
  // crucially refetches the hook did NOT run (React Query's reconnect/focus refetches, bell
  // pagination): a row already in the cache before a frame must count as seen no matter how it
  // got there, or the next frame's diff replays it as a toast. Permanent for the hook's lifetime;
  // a frame's own delivery still toasts because its run diffs against the snapshot taken before
  // its refetch (see refetchFeed).
  useEffect(() => {
    if (!enabled) return undefined;

    const converge = (data: NotificationsFeedData | undefined) => {
      if (!data) return;
      seenIdsRef.current ??= new Set();
      const seen = seenIdsRef.current;
      data.pages.forEach((page) => page.data.forEach((row) => seen.add(row.id)));
    };

    converge(queryClient.getQueryData<NotificationsFeedData>(notificationQueryKeys.index({})));
    const feedHash = hashKey(notificationQueryKeys.index({}));
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryHash !== feedHash) return;
      converge(event.query.state.data as NotificationsFeedData | undefined);
    });
  }, [enabled, queryClient]);

  // Browser connectivity opens the suspect window the instant it's lost — that signal fires even
  // when the socket dies too quietly for the cable's own monitor to notice in time (the
  // DevTools-offline case above). The cable's onDisconnected below covers the reverse blind spot:
  // a dead connection the OS never flags (wifi stall, server restart). Subscribed through
  // TanStack's onlineManager rather than raw window listeners: it's the same notion of "offline"
  // that already pauses the query client's own fetching, and it dedupes repeated same-state
  // events.
  //
  // Going online closes the window after the same grace a confirmed connect gets: a blip the
  // socket survives fires offline/online with no cable disconnect/connect ever following, and a
  // window only a connect could close would stay open for the rest of the session — every live
  // toast silently dead. Closing on online is still safe against the buffered flush: a dying
  // socket's frames flush at the restore instant and clear the nudge debounce well inside the
  // grace, while a frame arriving after it on a surviving socket is genuinely live.
  useEffect(() => {
    if (!enabled) return undefined;

    return onlineManager.subscribe(() => {
      if (!onlineManager.isOnline()) {
        openSuspectWindow();
        return;
      }
      closeSuspectWindow();
    });
  }, [enabled, openSuspectWindow, closeSuspectWindow]);

  // Open a toast's target with the panel row's one-action semantic, mirrored from
  // NotificationInbox.handleOpen: mark read if unread, then SPA-navigate. The toast itself slides
  // out via the provider's animated dismiss (wired to the action click there).
  const handleToastClick = useCallback(
    (notification: Notification) => {
      if (notification.isUnread) updateNotification.mutate({ id: notification.id, read: true });
      const { targetUrl } = notification.notificationEvent;
      if (targetUrl) navigate(targetUrl);
    },
    [navigate, updateNotification]
  );

  const addToast = useCallback(
    (notification: Notification) => {
      addNotification({
        // The Notification row's own uuid — deterministic, and unique by the same unique
        // index that wrote the row (the seen-set already prevents double-toasting it).
        id: notification.id,
        state: NotificationState.NOTIFICATION,
        title: notification.notificationEvent.message,
        // The inbox row's kind chip, 1:1 (Taylor, story-7 review).
        icon: (
          <NotificationKindChip kind={notification.notificationEvent.kind} tone={notification.notificationEvent.tone} />
        ),
        // The row's link convention — patient name, "View" fallback (never the AI-job default).
        actionSuccessText: notification.notificationEvent.linkLabel,
        onActionClick: () => handleToastClick(notification),
        onDismiss: () => {},
        autoDismissMs: NOTIFICATION_TOAST_TTL_MS,
      });
    },
    [addNotification, handleToastClick]
  );

  // Refetch the feed (badge + both tabs, via the shared index prefix); when toastDiff is set —
  // only ever for a frame-triggered run — toast each unread row this run delivered, diffed
  // against the seen-set as it stood when the run started. A null snapshot (a frame raced the
  // login fetch — no fetch has populated the feed yet) forces the run silent: the backlog it
  // pulls becomes baseline via the convergence subscription, never toasts (the burst-on-login
  // bug). A nudge arriving mid-refetch defers to a trailing refetch so its row is never dropped.
  // (A frame in a race window gets absorbed into the baseline — the row still lands in the bell;
  // a lost toast beats replaying a backlog.)
  //
  // KNOWN RESIDUAL (pre-existing — every version of this hook bursts here): a FAILED refetch
  // leaves a stale non-null baseline, and the next frame's run delivers the recovery payload
  // through its own fetch, where it is indistinguishable from a live burst — the accrued rows
  // toast. Needs its own story; a content-free nudge cannot close it.
  const refetchFeed = useCallback(
    (toastDiff: boolean) => {
      if (inFlightRef.current) {
        pendingToastRef.current = pendingRef.current ? pendingToastRef.current && toastDiff : toastDiff;
        pendingRef.current = true;
        return;
      }
      inFlightRef.current = true;

      const snapshot = seenIdsRef.current === null ? null : new Set(seenIdsRef.current);
      const epochAtStart = suspectEpochRef.current;
      const beforeUpdateCount = queryClient.getQueryState(notificationQueryKeys.index({}))?.dataUpdateCount ?? 0;

      queryClient
        .invalidateQueries({ queryKey: notificationQueryKeys.index() })
        .then(() => {
          const after = queryClient.getQueryData<NotificationsFeedData>(notificationQueryKeys.index({}));
          // invalidateQueries resolves even when the fetch FAILED (its refetch catches per-query
          // errors) — dataUpdateCount moving is the proof this run has fresh data to diff, and a
          // count (not dataUpdatedAt) so a pinned test clock can't mask it. A fetch cancelled by
          // a concurrent invalidation still counts as delivered: query-core resolves a silent
          // cancel with the replacement fetch's data (the piggyback), so the frame's toast rides
          // through with no rescue machinery.
          const delivered =
            !!after &&
            (queryClient.getQueryState(notificationQueryKeys.index({}))?.dataUpdateCount ?? 0) > beforeUpdateCount;

          // The suspect window is re-checked at settle time (a stalled request can outlive the
          // grace), and the epoch guard catches a window that opened AND closed mid-flight —
          // a settle-time point sample alone would read that as live.
          const firstPage = after?.pages[0]?.data ?? [];
          const newUnread =
            toastDiff &&
            snapshot !== null &&
            delivered &&
            suspectEpochRef.current === epochAtStart &&
            Date.now() >= suspectUntilRef.current
              ? firstPage.filter((row) => row.isUnread && !snapshot.has(row.id))
              : [];

          // Only fold a response we actually got — folding `after: undefined` through
          // `??= new Set()` would turn the protective null baseline into an EMPTY one, and the
          // next frame after the feed recovers would toast the whole backlog.
          if (after) {
            seenIdsRef.current ??= new Set();
            const seen = seenIdsRef.current;
            after.pages.forEach((page) => page.data.forEach((row) => seen.add(row.id)));
          }

          // Oldest first so the newest toast lands on top of the stack (the store prepends).
          newUnread.reverse().forEach((row) => addToast(row));
        })
        .finally(() => {
          inFlightRef.current = false;
          // A request landed while this refetch was in flight — run once more to catch its rows.
          if (pendingRef.current) {
            pendingRef.current = false;
            refetchFeed(pendingToastRef.current);
          }
        });
    },
    [queryClient, addToast]
  );

  useDebouncedWebsocketSubscription<NotificationChannelMessage>({
    channel: 'NotificationChannel',
    debounceMs: NUDGE_DEBOUNCE_MS,
    enabled,
    onConnected: () => {
      // A confirmed connect closes the suspect window after a short grace (never opens one: a
      // clean session's window is closed, so its frames toast with no warm-up).
      closeSuspectWindow();
      if (!everConnected.current) {
        everConnected.current = true;
        return;
      }
      // Reconnect resync: converge whatever a cable drop missed — silently. Those rows did not
      // arrive while live, so they belong in the badge, not the toast stack.
      refetchFeed(false);
    },
    onDisconnected: () => {
      openSuspectWindow();
    },
    // A frame received inside the suspect window is a stale signal (buffered by a dying socket),
    // not a live announcement — downgrade it to the silent path.
    onReceived: () => refetchFeed(Date.now() >= suspectUntilRef.current),
  });
}
